/**
 * Dev/QA-only runtime override layer — the highest-precedence layer.
 *
 * Two sources, both read once and cached (flipping an override needs a reload):
 *   - localStorage `featureFlagOverrides`: `{"PERPETUAL_SPOT": false}`
 *   - query param `?ff=PERPETUAL_SPOT:false,OPTIONS:true`
 *
 * Only active outside production builds, or in a production build that opts in
 * with `REACT_APP_FEATURE_FLAG_DEV_OVERRIDES=true` (staging QA). Both operands
 * of `DEV_OVERRIDES_ALLOWED` are inlined by CRA's DefinePlugin, so in a normal
 * production build it folds to `false` and the storage/query-param readers below
 * are dropped by Terser.
 *
 * Unlike the env layer this one WARNS instead of throwing: it is a developer
 * convenience driven by a hand-typed URL, never a deployment's configuration.
 */
import { LocalStorageKeyEnum } from "../../enums/localStorage";
import { FeatureFlagConfigError } from "./errors";
import { FEATURE_FLAGS } from "./registry";
import { FeatureFlagKey } from "./types";

const QUERY_PARAM = "ff";

// Module-scope const, not a function body: both operands are compile-time
// literals after DefinePlugin, so Terser can fold this to `false` and then
// prune the `if` in `readDevOverrides` along with everything it references.
//
// The opt-in var is parsed as strictly as every REACT_APP_FEATURE_* flag —
// it is documented in the same block — so `=TRUE` or `=1` fails loudly rather
// than silently leaving QA overrides switched off.
const DEV_OVERRIDES_ENV = process.env.REACT_APP_FEATURE_FLAG_DEV_OVERRIDES;
if (DEV_OVERRIDES_ENV !== undefined && DEV_OVERRIDES_ENV !== "true" && DEV_OVERRIDES_ENV !== "false") {
  throw new FeatureFlagConfigError(
    "REACT_APP_FEATURE_FLAG_DEV_OVERRIDES must be exactly \"true\" or \"false\", " +
      `got ${JSON.stringify(DEV_OVERRIDES_ENV)}. Remove it to use the default (false).`
  );
}
const DEV_OVERRIDES_ALLOWED =
  process.env.NODE_ENV !== "production" || DEV_OVERRIDES_ENV === "true";

export const areDevOverridesAllowed = (): boolean => DEV_OVERRIDES_ALLOWED;

let cache: Partial<Record<FeatureFlagKey, boolean>> | undefined;

const isKnownKey = (key: string): key is FeatureFlagKey =>
  Object.prototype.hasOwnProperty.call(FEATURE_FLAGS, key);

const warn = (message: string) => {
  // eslint-disable-next-line no-console
  console.warn(`[FeatureFlags] dev override ignored: ${message}`);
};

const assign = (
  target: Partial<Record<FeatureFlagKey, boolean>>,
  rawKey: string,
  rawValue: unknown
) => {
  if (!isKnownKey(rawKey)) {
    warn(`unknown flag "${rawKey}"`);
    return;
  }
  if (rawValue === true || rawValue === "true") {
    target[rawKey] = true;
    return;
  }
  if (rawValue === false || rawValue === "false") {
    target[rawKey] = false;
    return;
  }
  warn(`${rawKey} must be true or false, got ${JSON.stringify(rawValue)}`);
};

const readStorage = (target: Partial<Record<FeatureFlagKey, boolean>>) => {
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(LocalStorageKeyEnum.FEATURE_FLAG_OVERRIDES);
  } catch {
    return; // Storage blocked (private mode, embedded context) — no overrides.
  }
  if (!raw) {
    return;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    warn(`${LocalStorageKeyEnum.FEATURE_FLAG_OVERRIDES} is not valid JSON`);
    return;
  }
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    warn(`${LocalStorageKeyEnum.FEATURE_FLAG_OVERRIDES} must be a JSON object`);
    return;
  }
  Object.entries(parsed as Record<string, unknown>).forEach(([key, value]) =>
    assign(target, key, value)
  );
};

const readQueryParam = (target: Partial<Record<FeatureFlagKey, boolean>>) => {
  let raw: string | null = null;
  try {
    raw = new URLSearchParams(window.location.search).get(QUERY_PARAM);
  } catch {
    return;
  }
  if (!raw) {
    return;
  }
  raw
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .forEach((entry) => {
      const separatorIndex = entry.indexOf(":");
      if (separatorIndex <= 0) {
        warn(`"${entry}" is not in KEY:true|false form`);
        return;
      }
      assign(
        target,
        entry.slice(0, separatorIndex),
        entry.slice(separatorIndex + 1)
      );
    });
};

const readDevOverrides = (): Partial<Record<FeatureFlagKey, boolean>> => {
  if (cache) {
    return cache;
  }
  const overrides: Partial<Record<FeatureFlagKey, boolean>> = {};
  if (DEV_OVERRIDES_ALLOWED && typeof window !== "undefined") {
    readStorage(overrides);
    readQueryParam(overrides);
  }
  cache = overrides;
  return overrides;
};

export const getFeatureFlagDevOverride = (
  key: FeatureFlagKey
): boolean | undefined => readDevOverrides()[key];

/** Test-only: drop the cached overrides so the next read re-parses. */
export const resetFeatureFlagDevOverrides = (): void => {
  cache = undefined;
};
